import Style from './formTypeProvider.module.scss';
import { useState, useMemo, useCallback, FC } from 'react';
import { GeneralFormBuilderProps } from 'src/features/generalForms/builder/generalFormBuilder.t';
import { useStudyNoteBuilderProps } from 'src/features/generalForms/studyNoteForms/hooks/useStudyNoteBuilderProps';
import { AppDropDownWithSuggestionInput } from 'uikit/inputs';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { first } from 'lodash';
import { useMonitoringFormBuilderProps } from 'src/features/generalForms/monitoringForms/hooks/useMonitoringFormBuilderProps';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { AppButton } from 'uikit/buttons';
import useLayoutImport from 'src/components/formEditor/toolbox/importLayout/useLayoutImport';
import { logger } from 'src/application/logging/logging';
import { showErrorToast } from 'src/components/toast/toast-helper';

export const formVariants = ['notes', 'monitoring'] as const;
export type FormVariants = (typeof formVariants)[number];

export type SelectorConfigProps = {
  options: DropdownOption<number>[];
  value?: DropdownOption<number>;
  onChange?: (newValue: DropdownOption<number> | undefined) => void;
  isRequired?: boolean;
};

export type BuilderDataWithSelectorProps = {
  builderProps: GeneralFormBuilderData;
  selectorProps: SelectorConfigProps;
  createConfigModal: JSX.Element;
};

export type GeneralFormBuilderData = Pick<
  GeneralFormBuilderProps,
  | 'defaultLayout'
  | 'initialScoreSections'
  | 'initialVariables'
  | 'onDelete'
  | 'onSave'
  | 'uploadFileFunction'
  | 'loading'
  | 'formConfigSettingPanel'
  | 'onSaveAs'
>;

export const useFormTypeSelector = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.TopBar.SelectConfigSection');
  const options = useMemo(
    () =>
      [
        {
          key: 'notes',
          text: t('Selector.StudyNotesConfig'),
        },
        {
          key: 'monitoring',
          text: t('Selector.MonitoringFormsConfig'),
        },
      ] satisfies DropdownOption<FormVariants>[],
    [t],
  );
  const [selectedFormType, setSelectedFormType] = useState<DropdownOption<FormVariants>>(first(options)!);

  const notesFormData = useStudyNoteBuilderProps({ enabled: selectedFormType.key === 'notes' });
  const monitoringFormsData = useMonitoringFormBuilderProps({ enabled: selectedFormType.key === 'monitoring' });

  const formTypeData: Record<FormVariants, BuilderDataWithSelectorProps> = {
    ['notes']: notesFormData,
    ['monitoring']: monitoringFormsData,
  };

  return {
    configSelectorComponent: (
      <div className={Style.container}>
        <AppDropDownWithSuggestionInput<DropdownOption<FormVariants>>
          isRequired
          value={selectedFormType}
          options={options}
          onChange={(x) => setSelectedFormType(x ?? first(options)!)}
        />
        {/* @ts-ignore */}
        <AppDropDownWithSuggestionInput<DropdownOption>
          {...formTypeData[selectedFormType.key].selectorProps}
          placeholder={t('SelectConfigPlaceholder')}
        />
        <ImportLayoutComponent
          onImport={() => {
            if (selectedFormType.key !== 'notes') {
              // We mustn't reset selector for notes form type,
              // because this form is single type. Only one note per study is allowed.
              formTypeData[selectedFormType.key].selectorProps.onChange?.(undefined);
            }
          }}
        />
      </div>
    ),
    builderProps: formTypeData[selectedFormType.key].builderProps,
    createConfigModal: formTypeData[selectedFormType.key].createConfigModal,
  };
};

const ImportLayoutComponent: FC<{ onImport: () => void }> = ({ onImport }) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.TopBar.SelectConfigSection');
  const { importLayout } = useLayoutImport();

  const importLayoutHandler = useCallback(async () => {
    try {
      await importLayout({
        onBeforeImport: onImport,
      });
    } catch (error: any) {
      logger().error(error);
      showErrorToast(error.message, t('ImportErrorToast'), 5000);
    }
  }, [importLayout, onImport, t]);

  return (
    <AppButton
      colorSchema={'primary'}
      variant={'icon-link'}
      text={t('ImportLayoutButton')}
      onClick={importLayoutHandler}
    />
  );
};
